import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import useCategories from "../../../utils/useCategories";
import { disableSwitcher } from "../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import { setHideBreadCrumb } from "../../../redux/features/hideBreadCrumb/hideBreadCrumb";

// Component
import StatusContainer from "../../../components/_utils/CommonStatus/StatusContainer";
import TabsWeapperSection from "../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import InfoCard from "../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import FormatAmount from "../../../components/_utils/FormatAmount/FormatAmount";
import { formatAccountNumber } from "../../../components/_utils/FormatAccountNumber";
import CommonToast from "../../../components/_utils/CommonToast";
import SmallPopupWithBulletPoints from "../../../components/_utils/CommonSmallPopupWithBulletPoint";
import TEXT from "../../../utils/globalText";
import {
  GlobalConfiguration,
  RejectReasonLength,
} from "../../../utils/configurationProvider";
import {
  fieldForceTxnDeniedApproval,
  fieldForceTxnRequestSuccessDetail,
  fieldForceTxnWaitingApproval,
} from "../../../redux/features/fieldForceTransaction";
import ICONS from "../../../assets/Icons";

const FieldForceTxnSuccess = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { id } = useParams();
  const {
    fieldForceTxnSuccessRequestData,
    fieldForceTxnSuccessRequestLoading,
  } = useSelector((state) => state.fieldForceTransaction);

  const categoryMapper = useCategories();
  const location = useLocation();
  const Requestlocation = location?.state;
  const request = fieldForceTxnSuccessRequestData?.request?.[0];
  const categoryName = request?.category_id?.category_name || request?.category;
  const categoryIcon = categoryMapper(request?.category_id?.category_id);
  const comment =
    request?.comment ||
    request?.comments ||
    request?.field_force_transactions_details?.comments;

  const services = useSelector((state) => state.refreshToken.services);
  const hasApprovalService = services?.includes("AFieldForceTxn");
  const txnId =
    request?.field_force_transactions_details?.field_force_transaction_id ||
    Requestlocation?.Txn_id;

  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [isReasonFocused, setIsReasonFocused] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    dispatch(disableSwitcher());
  }, []);

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fieldForceTxnRequestSuccessDetail(id));
  }, [id]);

  const goToApproval = () => {
    navigate(
      "/field_force_transaction_dashboard/field_force_request_single_approval",
    );
  };

  const dashboardHandler = () => {
    navigate("/field_force_transaction_dashboard");
  };

  const txnDetailPath = `/field_force_transaction_dashboard/field_force_transaction_view_all/field_force_transaction_detail_page/${txnId}`;

  const showToast = (text, variant, icon) => {
    toast.dismiss();
    CommonToast({
      text,
      icon,
      variant,
      btnProps: {
        isDisabled: false,
        enabledTextColor: "var(--text-primary)",
        enabledBgColor: "var(--primary-bg)",
      },
    });
  };

  const showErrorToast = (text) =>
    showToast(
      text || "An error occurred while taking action on this transaction",
      "var(--error)",
      ICONS?.ERROR_WITH_WHITE_CIRCLE,
    );

  const getErrorReason = (payload) => {
    const base = payload?.errors?.base?.[0];
    const reason = base?.errors?.[0]?.reason ?? base?.reason ?? base;
    return Array.isArray(reason) ? reason[0] : reason;
  };

  const handleApprove = async () => {
    if (actionLoading || !txnId) return;
    setActionLoading(true);
    showToast("Approving transaction", "var(--processing)", ICONS?.LOADER_GIF);

    const res = await dispatch(fieldForceTxnWaitingApproval(txnId));
    const payload = res?.payload;
    setActionLoading(false);

    if (payload?.errors?.user_unauthorized?.[0]) {
      toast.dismiss();
      navigate("/feature_not_assigned", {
        state: { unavailableService: "AFieldForceTxn" },
      });
      return;
    }

    if (!payload || payload?.status === false) {
      showErrorToast(getErrorReason(payload));
      return;
    }

    showToast(
      "Transaction approved successfully.",
      "var(--success)",
      ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
    );
    navigate(txnDetailPath);
  };

  const handleReject = async () => {
    if (
      actionLoading ||
      !txnId ||
      declineReason?.trim()?.length <
        GlobalConfiguration?.owneraccountnormalLength
    )
      return;
    setActionLoading(true);
    showToast("Rejecting transaction", "var(--processing)", ICONS?.LOADER_GIF);

    const res = await dispatch(
      fieldForceTxnDeniedApproval({ id: txnId, AccountIdData: declineReason }),
    );
    const payload = res?.payload;
    setActionLoading(false);

    if (payload?.errors?.user_unauthorized?.[0]) {
      toast.dismiss();
      navigate("/feature_not_assigned", {
        state: { unavailableService: "AFieldForceTxn" },
      });
      return;
    }

    if (payload?.status !== true) {
      showErrorToast(payload?.error || getErrorReason(payload));
      return;
    }

    setShowRejectPopup(false);
    setDeclineReason("");
    showToast(
      "Transaction rejected successfully.",
      "var(--success)",
      ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
    );
    navigate(txnDetailPath);
  };

  const handleReasonInput = (e) => {
    const value = e.target.value
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .slice(
        GlobalConfiguration.lengthCheckZero,
        RejectReasonLength.lengthOfDecline,
      );
    setDeclineReason(value);
  };

  const reasonVariant = () => {
    const length = declineReason?.trim()?.length;
    if (!length) return "default";
    if (length >= GlobalConfiguration?.rejectReasonMinLength) return "verified";
    return isReasonFocused ? "typing" : "error";
  };

  // Button

  const buttonRows = [
    ...(hasApprovalService
      ? [
          [
            {
              btnText: "Reject account",
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => setShowRejectPopup(true),
              isDisabled: actionLoading,
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
            {
              btnText: "Approve account",
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: handleApprove,
              isDisabled: actionLoading,
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
          ],
        ]
      : []),
    [
      {
        btnText: "Go to request approval",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS?.GO_TO_APPROVAL,
        handleFunction: goToApproval,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-card)",
      },
      {
        btnText: "View transaction details",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS?.VIEW_DETAILS,
        handleFunction: () => navigate(txnDetailPath),
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },
    ],
    [
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        handleFunction: dashboardHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: "white",
      },
    ],
  ];

  return (
    <>
      {showRejectPopup && (
        <div className="fixed top-top-base left-left-base w-full h-full bg-custom-backdrop bg-opacity-dark-opacity flex items-center justify-center z-z-index-modal">
          <SmallPopupWithBulletPoints
            headerTitle={TEXT?.REJECT_REASON_TXN_TITLE}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-md"
            subtext={TEXT?.PROVIDE_REJECT_REQUEST_REASON_PROMPT}
            bulletPoints={[
              "Transaction will be rejected, and distribution will not be initiated.",
              "Transaction cannot be edited.",
            ]}
            inputAdornment={false}
            inputVariant={reasonVariant()}
            inputOnFocus={() => setIsReasonFocused(true)}
            inputOnBlur={() => setIsReasonFocused(false)}
            inputBgColor="var(--primary-bg)"
            inputFgColor="var(--text-secondary)"
            inputOnInput={(e) => {
              if (!actionLoading) handleReasonInput(e);
            }}
            inputValue={declineReason}
            inputFontKind="big"
            inputIsFullWidth={true}
            btnOneText={TEXT?.CANCEL}
            btnOneHandleFunction={() => {
              if (actionLoading) return;
              setShowRejectPopup(false);
              setDeclineReason("");
            }}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneHoverOption="white"
            btnOneIsDisabled={actionLoading}
            btnTwoText={TEXT?.REJECT}
            btnTwoHandleFunction={handleReject}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={
              declineReason?.trim()?.length <
              GlobalConfiguration?.owneraccountnormalLength
            }
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoIsLoaderNeeded={actionLoading}
            btnTwoIsLoading={actionLoading}
            btnTwoHoverOption="black"
            btnTwoInActiveVariantNeeded={true}
            noteText={TEXT?.REJECT_POPUP_ACCOUNT_NOTE_TEXT}
            isTextInputField={true}
            inputPlaceholder={TEXT?.REASON}
            subtextwithbullet="Y"
          />
        </div>
      )}
      <StatusContainer
        isLoading={fieldForceTxnSuccessRequestLoading}
        status="success"
        hoverOption="white"
        title={`${
          Requestlocation?.type === "PayFieldForceTxn"
            ? "Request approved and transaction created successfully."
            : "Field force Request Created successfully."
        }`}
        buttonRows={buttonRows}
      >
        <TabsWeapperSection title="Transaction details">
          <>
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: `INR ${FormatAmount({ price: request?.approved_amount || "0" })}`,
                  leftSubTitle: "Approved amount",
                  leftTextFontWeight: "semibold",
                },
              ]}
            />

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: `INR ${FormatAmount({ price: request?.requested_amount || "0" })}`,
                  leftSubTitle: "Requested amount",
                  leftTextFontWeight: "semibold",
                },
              ]}
            />

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: request?.field_force_account_name,
                  leftSubTitle: "Account name",
                },
              ]}
            />

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: categoryName || "Not given",
                  leftSubTitle: "Category",
                  icon: categoryIcon?.url,
                  iconColor: categoryIcon?.colour,
                },
              ]}
            />

            {request?.lifecycle_status === "approved" && (
              <InfoCard
                bgColor="var(--positive-card)"
                onClick={() =>
                  navigate(
                    `/owner_account_dashboard/owner_accounts_view_all/owner_accounts_detail_page/${request?.owner_account_id}`,
                  )
                }
                rows={[
                  {
                    leftTitle: formatAccountNumber(
                      request?.owner_account_bank_acc_no,
                    ),
                    leftSubTitle: TEXT?.SOURCE_ACCOUNT_NUMBER,
                    ifsc: request?.owner_account_ifsc_code,
                    rightIcon: true,
                  },
                ]}
                hoverable="black"
              />
            )}

            <InfoCard
              bgColor="var(--positive-card)"
              onClick={() =>
                navigate(
                  `/field_force_account_dashboard/field_force_account_view_all/field_force_account_detail_page/${request?.field_force_account_id}`,
                )
              }
              rows={[
                {
                  leftTitle: formatAccountNumber(
                    request?.field_force_account_bank_acc_no,
                  ),
                  leftSubTitle: TEXT?.DESTINATION_ACCOUNT_NUMBER,
                  ifsc: request?.field_force_account_ifsc_code,
                  rightIcon: true,
                },
              ]}
              hoverable="black"
            />

            {/* <InfoCard
              bgColor="var(--positive-card)"
              showRowDivider={true}
              rowBorderColor="var(--positive-bg)"
              rows={[
                {
                  leftTitle: request?.field_force_user_full_name || "Not given",
                  leftSubTitle: "Username",
                },
                {
                  leftTitle: request?.field_force_identifier || "Not given",
                  leftSubTitle: "User ID",
                },
              ]}
            /> */}

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: request?.labels?.length
                    ? request.labels.join(", ")
                    : "Not assigned",
                  leftSubTitle: "Labels (optional)",
                },
              ]}
            />

            {comment?.trim() && (
              <InfoCard
                bgColor="var(--positive-card)"
                rows={[
                  {
                    leftTitle: comment,
                    leftSubTitle: "Comment",
                  },
                ]}
              />
            )}

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle:
                    request?.field_force_transactions_details
                      ?.field_force_transaction_id,
                  leftSubTitle: "Transaction ID",
                  copyText:
                    request?.field_force_transactions_details
                      ?.field_force_transaction_id,
                },
              ]}
            />
          </>
        </TabsWeapperSection>
      </StatusContainer>
    </>
  );
};

export default FieldForceTxnSuccess;
